import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STATUS, KINDS } from "@/components/documents/docConst";

const WEEK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthName = (d, opt = "long") => d.toLocaleDateString("it-IT", { month: opt, year: opt === "long" ? "numeric" : "2-digit" });
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const Strip = ({ docs, month, onPick }) => {
  const start = addMonths(new Date(), 0);
  const months = Array.from({ length: 12 }, (_, i) => addMonths(start, i));
  const counts = months.map((m) => docs.filter((d) => d.due_date?.startsWith(ym(m))));
  const max = Math.max(1, ...counts.map((c) => c.length));
  return (
    <div className="mb-5 grid grid-cols-6 gap-1 sm:grid-cols-12" data-testid="docs-calendar-strip">
      {months.map((m, i) => {
        const urgent = counts[i].some((d) => ["EXPIRED", "TODAY", "D7"].includes(d.status));
        return (
          <button key={ym(m)} type="button" onClick={() => onPick(m)} data-testid={`docs-strip-${ym(m)}`}
            className={`flex flex-col items-center border bg-white px-1 pb-2 pt-3 transition-colors hover:border-foreground ${ym(m) === ym(month) ? "border-foreground ring-1 ring-foreground" : ""}`}>
            <div className="flex h-10 w-full items-end justify-center">
              <div className={`w-3 ${counts[i].length ? (urgent ? "bg-red-600" : "bg-foreground") : "bg-muted"}`} style={{ height: `${Math.max(8, (counts[i].length / max) * 100)}%` }} />
            </div>
            <div className="mt-1 font-mono text-sm font-bold" data-testid={`docs-strip-count-${ym(m)}`}>{counts[i].length}</div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{monthName(m, "short")}</div>
          </button>
        );
      })}
    </div>
  );
};

const DayCell = ({ day, items, inMonth, isToday, onOpen }) => (
  <div className={`min-h-[96px] border-b border-r p-1.5 ${inMonth ? "bg-white" : "bg-muted/30 text-muted-foreground"}`} data-testid={`docs-day-${day}`}>
    <div className={`mb-1 inline-flex h-6 w-6 items-center justify-center font-mono text-xs ${isToday ? "rounded-full bg-foreground text-background" : ""}`}>{Number(day.slice(8))}</div>
    {items.slice(0, 3).map((d) => (
      <button key={d.id} type="button" onClick={() => onOpen(d.id)} title={`${KINDS[d.kind]} · ${d.title}`} data-testid={`docs-cal-item-${d.id}`}
        className={`mb-0.5 block w-full truncate px-1.5 py-0.5 text-left text-[11px] font-medium transition-opacity hover:opacity-80 ${STATUS[d.status][1]}`}>{d.title}</button>
    ))}
    {items.length > 3 && <div className="px-1 text-[10px] text-muted-foreground">+{items.length - 3} altri</div>}
  </div>
);

export const DocumentsCalendar = ({ docs, onOpen }) => {
  const [month, setMonth] = useState(addMonths(new Date(), 0));
  const byDay = useMemo(() => docs.reduce((acc, d) => { if (d.due_date) (acc[d.due_date] ||= []).push(d); return acc; }, {}), [docs]);
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7;
  const days = Array.from({ length: Math.ceil((startOffset + new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()) / 7) * 7 }, (_, i) => {
    const d = new Date(month.getFullYear(), month.getMonth(), i - startOffset + 1);
    return `${ym(d)}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const todayIso = `${ym(new Date())}-${String(new Date().getDate()).padStart(2, "0")}`;
  const inMonth = docs.filter((d) => d.due_date?.startsWith(ym(month))).length;
  return (
    <div data-testid="docs-calendar">
      <div className="micro-label mb-2">Rinnovi e revisioni nei prossimi 12 mesi</div>
      <Strip docs={docs} month={month} onPick={setMonth} />
      <div className="mb-3 flex items-center gap-2">
        <Button size="icon" variant="outline" onClick={() => setMonth(addMonths(month, -1))} data-testid="docs-cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" onClick={() => setMonth(addMonths(month, 1))} data-testid="docs-cal-next"><ChevronRight className="h-4 w-4" /></Button>
        <h2 className="ml-2 font-display text-lg font-bold capitalize" data-testid="docs-cal-month">{monthName(month)}</h2>
        <span className="text-sm text-muted-foreground">· {inMonth} {inMonth === 1 ? "scadenza" : "scadenze"}</span>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setMonth(addMonths(new Date(), 0))} data-testid="docs-cal-today">Oggi</Button>
      </div>
      <div className="overflow-x-auto">
        <div className="grid min-w-[700px] grid-cols-7 border-l border-t">
          {WEEK.map((w) => <div key={w} className="micro-label border-b border-r bg-muted/50 px-2 py-2">{w}</div>)}
          {days.map((day) => <DayCell key={day} day={day} items={byDay[day] || []} inMonth={day.startsWith(ym(month))} isToday={day === todayIso} onOpen={onOpen} />)}
        </div>
      </div>
    </div>
  );
};
